import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import dashboardApi from '../../api/dashboardApi';
import distributorApi from '../../api/distributorApi';
import { uiChartNames, uiTabNames } from '../../config/constants';
import { setDashToDate, setDashFromDate, setDashDateString } from '../../reducers/DateReducer/DateActions';
import { linkNavigationBar, categoriesProductsNavigationBar, newLostLocationsNavigationBar } from '../../reducers/UIReducer/UIActions';
import { setDashboardHighlights, setDashboardNewLocations, setDashboardLostLocations, setDashboardTopCategories, setDashboardTopProducts, setTableDataLoading, setChartDataLoading, setDistributorBreakdownChartData, setCompetitiveInsightsChartData, setBepDistributorListLoading, setBEPDistributorListData, setHighlightsLoading, setSelectedStatesFilter, setSelectedCategoriesFilter, setSelectedSegmentsFilter, setSelectedLocationsFilter, setSelectedChildrensFilter, setSelectedDistributorsFilter, setChildrenFilters } from '../../reducers/DashboardReducer/DashboardActions';
import LocalStorage from '../../sharedComponents/LocalStorage/LocalStorage';
import { setOppsFilters, setOpportunitiesData, setOpportunitiesTableData } from '../../reducers/OpportunitiesReducer/OpportunitiesActions';
import manufacturerApi from '../../api/manufacturerApi';
import datasuiteApi from '../../api/datasuiteApi';
import { toast } from 'react-toastify';
import moment from 'moment';
import useManufacturerData from "../../sharedComponents/useManufacturerData/useManufacturerData";

const useDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setDashboardTab = () => {
    dispatch(linkNavigationBar.setTab(uiTabNames.dashboard));
  }

  const handleClickTopCategoriesTab = () => {
    dispatch(categoriesProductsNavigationBar.setTab(uiTabNames.topCategories));
  }

  const handleClickTopProductsTab = () => {
    dispatch(categoriesProductsNavigationBar.setTab(uiTabNames.topProducts));
  }

  const handleClickNewLocationsTab = () => {
    dispatch(newLostLocationsNavigationBar.setTab(uiTabNames.newLocations));
  }

  const handleClickLostLocationsTab = () => {
    dispatch(newLostLocationsNavigationBar.setTab(uiTabNames.lostLocations));
  }

  const handleClickViewCampaignManagement = () => {
    navigate('/campaignmanagement');
  }

  const filterCsvTable = () => {
    // Declare variables
    var input, filter, table, tr, td, i, txtValue;
    input = document.getElementById("csvSearchInput");
    filter = input.value.toUpperCase();
    table = document.getElementById("distributorCsvTable");
    tr = table.getElementsByTagName("tr");

    // Loop through all table rows, and hide those who don't match the search query
    for (i = 0; i < tr.length; i++) {
      td = tr[i].getElementsByTagName("td")[0];
      if (td) {
        txtValue = td.textContent || td.innerText;
        if (txtValue.toUpperCase().indexOf(filter) > -1) {
          tr[i].style.display = "";
        } else {
          tr[i].style.display = "none";
        }
      }
    }
  }

  return {
    setDashboardTab,
    handleClickTopCategoriesTab,
    handleClickTopProductsTab,
    handleClickNewLocationsTab,
    handleClickLostLocationsTab,
    handleClickViewCampaignManagement,
    filterCsvTable
  }
}

const useDates = () => {
  const dispatch = useDispatch();
  const fromDate = useSelector(({dates}) => dates.dashFromDate);
  const toDate = useSelector(({dates}) => dates.dashToDate);
  const topCategoriesData = useSelector(({dashboard}) => dashboard.topCategories);
  const selectedStatesFilter = useSelector(({dashboard}) => dashboard.selectedStatesFilter);
  const selectedStates = selectedStatesFilter?.map((option) => option.value);
  const selectedCategoriesFilter = useSelector(({dashboard}) => dashboard.selectedCategoriesFilter);
  const selectedCategories = selectedCategoriesFilter?.map((option) => option.value);
  const selectedSegmentsFilter = useSelector(({dashboard}) => dashboard.selectedSegmentsFilter);
  const selectedSegments = selectedSegmentsFilter?.map((option) => option.value);
  const selectedLocationsFilter = useSelector(({dashboard}) => dashboard.selectedLocationsFilter);
  const selectedLocations = selectedLocationsFilter?.map((option) => option.value);
  const selectedChildrensFilter = useSelector(({dashboard}) => dashboard.selectedChildrensFilter);
  const selectedChildrens = selectedChildrensFilter?.map((option) => option.value);
  const selectedDistributorsFilter = useSelector(({dashboard}) => dashboard.selectedDistributorsFilter);
  const selectedDistributors = selectedDistributorsFilter?.map((option) => option.value);
  const { manufacturerParams, dataManagersParams } = useManufacturerData();

  const onChangeDashboardDates = (start, end) => {
    dispatch(setDashFromDate(moment(start).format('YYYY-MM-DD')));
    dispatch(setDashToDate(moment(end).format('YYYY-MM-DD')));
    const dateString = moment(start).format('YYYY-MM-DD') + " - " + moment(end).format('YYYY-MM-DD');
    dispatch(setDashDateString(dateString));
  }

  const onChangeSelectedStates = (states) => {
    dispatch(setSelectedStatesFilter(states));
  }

  const onChangeSelectedCategories = (categories) => {
    dispatch(setSelectedCategoriesFilter(categories));
  }

  const onChangeSelectedSegments = (segments) => {
    dispatch(setSelectedSegmentsFilter(segments));
  }

  const onChangeSelectedLocations = (locations) => {
    dispatch(setSelectedLocationsFilter(locations));
    fetchChildrenOptions(locations);
  }

  const fetchChildrenOptions = async (parentLocations) => {
    if (!Array.isArray(parentLocations) || parentLocations.length === 0) {
      dispatch(setChildrenFilters([]));
      dispatch(setSelectedChildrensFilter([]));
      return;
    }
    const parents = parentLocations.map(p => (typeof p === 'string' ? p : p?.value ?? p?.label ?? p?.name)).filter(Boolean);
    const payload = {
      manufacturerPlatformId: LocalStorage.manufacturerData.get().platform_id,
      chainData: true,
      emergingData: false,
      startDate: fromDate,
      endDate: toDate,
      parents,
    };
    try {
      const { data } = await datasuiteApi.filters.children.post(payload);
      const arr = Array.isArray(data) ? data : [];
      const sortedChildren = [...new Set(arr)].sort();
      dispatch(setChildrenFilters(sortedChildren));
      const validSelectedChildren = selectedChildrensFilter.filter(c => sortedChildren.includes(c));
      dispatch(setSelectedChildrensFilter(validSelectedChildren));
    } catch {
      dispatch(setChildrenFilters([]));
      dispatch(setSelectedChildrensFilter([]));
    }
  }

  const onChangeSelectedChildrens = (childrens) => {
    dispatch(setSelectedChildrensFilter(childrens));
  }

  const onChangeSelectedDistributors = (distributors) => {
    dispatch(setSelectedDistributorsFilter(distributors));
  }

  const getHighlights = () => {
    const params = {
      'startDate': fromDate,
      'endDate': toDate,
      'states': selectedStates,
      ...manufacturerParams
    }
    dispatch(setHighlightsLoading(true));
    dashboardApi.highlights.post(params).then(({data}) => {
      dispatch(setHighlightsLoading(false));
      dispatch(setDashboardHighlights(data));
    }).catch(err => {
      console.log(err);
    });
  }

  const getNewLocations = () => {
    const params = {
      'startDate': fromDate,
      'endDate': toDate,
      'states': selectedStates,
      'categories': selectedCategories,
      'segments': selectedSegments,
      'locations': selectedLocations,
      'childrens': selectedChildrens,
      'distributors': selectedDistributors,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.newLocations, true));
    dashboardApi.newLocations.post(params).then(data => {
      dispatch(setDashboardNewLocations(data.data));
      dispatch(setTableDataLoading(uiTabNames.newLocations, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getLostLocations = () => {
    const params = {
      'startDate': fromDate,
      'endDate': toDate,
      'states': selectedStates,
      'categories': selectedCategories,
      'segments': selectedSegments,
      'locations': selectedLocations,
      'childrens': selectedChildrens,
      'distributors': selectedDistributors,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.lostLocations, true));
    dashboardApi.lostLocations.post(params).then(data => {
      dispatch(setDashboardLostLocations(data.data));
      dispatch(setTableDataLoading(uiTabNames.lostLocations, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getTopCategories = () => {
    const params = {
      'startDate': fromDate,
      'endDate': toDate,
      'states': selectedStates,
      'categories': selectedCategories,
      'segments': selectedSegments,
      'locations': selectedLocations,
      'childrens': selectedChildrens,
      'distributors': selectedDistributors,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.topCategories, true));
    dashboardApi.topCategories.post(params).then(data => {
      dispatch(setDashboardTopCategories(data.data));
      dispatch(setTableDataLoading(uiTabNames.topCategories, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getTopProducts = () => {
    const params = {
      'startDate': fromDate,
      'endDate': toDate,
      'states': selectedStates,
      'categories': selectedCategories,
      'segments': selectedSegments,
      'locations': selectedLocations,
      'childrens': selectedChildrens,
      'distributors': selectedDistributors,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.topProducts, true));
    dashboardApi.topProducts.post(params).then(data => {
      dispatch(setDashboardTopProducts(data.data));
      dispatch(setTableDataLoading(uiTabNames.topProducts, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getSegmentOverview = () => {
    const params = {
      manufacturerPlatformId: LocalStorage.manufacturerData.get().platform_id,
      startDate: fromDate,
      endDate: toDate,
      states: selectedStates
    };

    dispatch({ type: 'SET_SEGMENT_OVERVIEW_LOADING', payload: { isLoading: true } });

    dashboardApi.segmentOverview.post(params)
      .then(({ data }) => {
        dispatch({ type: 'SET_DASHBOARD_SEGMENT_OVERVIEW', payload: { data }});
        dispatch({ type: 'SET_SEGMENT_OVERVIEW_LOADING', payload: { isLoading: false } });
      })
      .catch(err => console.log(err));
  };

  const getTotalOpportunitiesChartData = (filters) => {
    dispatch(setChartDataLoading(uiChartNames.totalOpportunitiesTable, true));
    var oppTypeFilters = "";
    var oppStatusFilters = "";
    if(filters?.length !== 0) {
        if(filters?.oppSourceStrings) {
          oppTypeFilters = filters.oppSourceStrings;
        }
        if(filters?.status) {
          oppStatusFilters = filters.status;
        }
    } else {
      dispatch(setOppsFilters([]));
    }
    const params = {
        'startDate': fromDate,
        'endDate': toDate,
        'complianceActivity': oppTypeFilters,
        "statuses": oppStatusFilters,
        ...manufacturerParams,
        ...dataManagersParams
    };
    manufacturerApi.agreements.post(params).then(({data}) => {
        dispatch(setOpportunitiesData(data.data.agreements));
        dispatch(setOpportunitiesTableData(data.data.tableData));
        dispatch(setChartDataLoading(uiChartNames.totalOpportunitiesTable, false));
    }).catch(err => {
        console.log(err);
        toast.error("Something went wrong loading your agreement data.", {autoClose: "5000"});
    });
  }

  const getDistributorBreakdownChartData = () => {
    const mfId = LocalStorage.manufacturerData.get().platform_id;
    dispatch(setChartDataLoading(uiChartNames.distributorBreakdownTable, true));
    const params = {
        'startDate': fromDate,
        'endDate': toDate,
        'states': selectedStates,
        'categories': selectedCategories,
        'segments': selectedSegments,
        'locations': selectedLocations,
        'childrens': selectedChildrens,
        'distributors': selectedDistributors,
        ...manufacturerParams
    };
    dashboardApi.charts.distributorBreakdown.post(params).then(({data}) => {
        dispatch(setDistributorBreakdownChartData(data));
        dispatch(setChartDataLoading(uiChartNames.distributorBreakdownTable, false));
    }).catch(err => {
        console.log(err);
        toast.error("Something went wrong loading your distributor data.", {autoClose: "5000"});
    });
  }

  const getCompetitiveInsightsChartData = () => {
    const mfId = LocalStorage.manufacturerData.get().platform_id;
    dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, true));
    if (topCategoriesData.length > 0) {
      const topCategories = topCategoriesData?.map(x => {return x.category});
      const params = {
        'startDate': fromDate,
        'endDate': toDate,
        'categories': (selectedCategories?.length > 0) ? selectedCategories : topCategories,
        'states': selectedStates,
        'segments': selectedSegments,
        'locations': selectedLocations,
        'childrens': selectedChildrens,
        'distributors': selectedDistributors,
        ...manufacturerParams
      };
      dashboardApi.charts.competitiveInsights.post(params).then(({data}) => {
        dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, false));
        dispatch(setCompetitiveInsightsChartData(data));
      }).catch(err => {
        console.log(err);
      });
    } else {
      dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, false));
    }
  }

  const getBEPDistributorListData = () => {
    dispatch(setBepDistributorListLoading(true));
    distributorApi.get().then(({data}) => {
      dispatch(setBepDistributorListLoading(false));
      dispatch(setBEPDistributorListData(data));
    }).catch(err => {
      console.log(err);
    });
  }

  const onClickDownloadCsv = (id) => {
    distributorApi.downloadCsv.get(id).then(({data}) => {
      const csv = data?.csvString;

      const removeHashtags = csv?.replace(/#/g, '');

      const dateSplit = data?.date.split('-');
      const formattedDate = `${dateSplit[0].padStart(2, '0')}_${dateSplit[1]}`;

      var hiddenElement = document.createElement('a');
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(removeHashtags);
      hiddenElement.target = '_blank';

      //provide the name for the CSV file to be downloaded
      hiddenElement.download = `BEP_Distributor_List_${formattedDate}.csv`;
      hiddenElement.click();
    });
  }


  const onChangeHighlightsFilter = (filter) => {
    let beplBusiness = true;
    let directBusiness = false;
    const params = {
      'startDate': fromDate,
      'endDate': toDate,
      'showBeplBusiness': beplBusiness,
      'showDirectBusiness': directBusiness,
      'states': selectedStates,
      ...manufacturerParams
    }
    dispatch(setHighlightsLoading(true));
    dashboardApi.highlights.post(params).then(({data}) => {
      dispatch(setHighlightsLoading(false));
      dispatch(setDashboardHighlights(data));
    }).catch(err => {
      console.log(err);
    });
  }

  const onApplySearch = () => {
    getHighlights();
    getNewLocations();
    getLostLocations();
    getTopCategories();
    getTopProducts();
    getDistributorBreakdownChartData();
    getCompetitiveInsightsChartData();
  }

  const onApplyDate = () => {
    getTotalOpportunitiesChartData();
  }

  return {
    setToDate: (d) => dispatch(setDashToDate(d)),
    setFromDate: (d) => dispatch(setDashFromDate(d)),
    toDate,
    fromDate,
    selectedStates,
    onApplySearch,
    onApplyDate,
    getHighlights,
    getNewLocations,
    getLostLocations,
    getTopCategories,
    getTopProducts,
    getTotalOpportunitiesChartData,
    getDistributorBreakdownChartData,
    getCompetitiveInsightsChartData,
    getBEPDistributorListData,
    onClickDownloadCsv,
    onChangeHighlightsFilter,
    onChangeDashboardDates,
    onChangeSelectedStates,
    onChangeSelectedCategories,
    onChangeSelectedSegments,
    onChangeSelectedLocations,
    onChangeSelectedChildrens,
    onChangeSelectedDistributors,
    getSegmentOverview 
  };
}

// eslint-disable-next-line
export default { useDates, useDashboard };
